import { apiClient, getData, getPage } from '@/api/client'
import type { components } from '@/api/schema'
import {
  analyticsReportSchema,
  bonusDraftSchema,
  buyDraftSchema,
  buyReceiptSchema,
  cashDividendDraftSchema,
  deletedProfileSchema,
  deletedTransactionSchema,
  dividendReceiptSchema,
  healthReportSchema,
  holdingSchema,
  importDraftSchema,
  importPreviewSchema,
  importResultSchema,
  importSaveDraftSchema,
  incomeReportSchema,
  needsDetailStatusSchema,
  portfolioOverviewSchema,
  portfolioSummarySchema,
  profileCreateDraftSchema,
  profileUpdateDraftSchema,
  profitLossReportSchema,
  sellDraftSchema,
  sellReceiptSchema,
  shareholderProfileSchema,
  snapshotPointSchema,
  transactionEditDraftSchema,
  transactionHistorySchema,
  updatedTransactionSchema,
  DEFAULT_VALUE_HISTORY_PERIOD,
  type AnalyticsReport,
  type BonusDraft,
  type BuyDraft,
  type BuyReceipt,
  type CashDividendDraft,
  type DeletedProfile,
  type DeletedTransaction,
  type DividendReceipt,
  type HealthReport,
  type Holding,
  type ImportDraft,
  type ImportPreview,
  type ImportResult,
  type ImportSaveDraft,
  type ImportSaveRow,
  type IncomeReport,
  type PortfolioOverview,
  type PortfolioSummary,
  type ProfileCreateDraft,
  type ProfileUpdateDraft,
  type ProfitLossReport,
  type SellDraft,
  type SellReceipt,
  type ShareholderProfile,
  type SnapshotPoint,
  type TransactionEditDraft,
  type TransactionGroup,
  type TransactionHistory,
  type UpdatedTransaction,
  type ValueHistoryPeriod,
} from '@/schemas/portfolio'

/**
 * The portfolio seam: nineteen routes (twenty-two method+path pairs) in, app
 * types out.
 *
 * Structured like `features/brokers/api/brokerService.ts`, for the same two
 * reasons. Wire types come from `@/api/schema` and are never restated, so a
 * regenerated `schema.d.ts` that renames a field breaks the build HERE, which
 * is where it should break; and every mapper's result goes through its Zod
 * schema before it leaves the module, so contract drift fails at the call
 * rather than three components later inside a `toFixed`.
 *
 * ── Three things about this API that silently corrupt behaviour ──────────────
 *
 * 1. `shareholder_id` is a QUERY parameter on the four write endpoints, not a
 *    body field. The views read `request.query_params`; a body field of that
 *    name is dropped by DRF and the write lands on the member's PRIMARY
 *    profile instead. Someone recording a buy against their spouse's portfolio
 *    would silently file it under their own. Every write below therefore takes
 *    `shareholderId` and puts it in `params.query`, never in `body`, and
 *    `scope()` is the one place that decision is made.
 *
 * 2. THREE naming schemes for the same four kinds of transaction. Bonus is
 *    POSTed to `transactions/bonus-dividend/` but edited and deleted under the
 *    group `bonus_shares`; cash dividend is POSTed to `cash-dividend/` and
 *    edited under `cash_dividends`. The ledger response keys are a third set
 *    (`purchases`, `sells`, `bonus_shares`, `cash_dividends`). No rule connects
 *    them, so `TRANSACTION_GROUP_BY_KIND` below is a literal table and a group
 *    is never assembled from a display label.
 *
 * 3. THREE things the OpenAPI document still gets wrong or omits, all of them
 *    drf-spectacular blind spots in mm-be. Each is worked around in this file
 *    and `src/api/schema.d.ts` is left alone — it is regenerated wholesale by
 *    `pnpm gen:api`, so an edit there would be silently reverted. The only
 *    durable fixes are on the views; see BACKEND CHANGES NEEDED.
 *
 *      WORKAROUND 1  `PATCH /transactions/{group}/{id}/` advertises one body
 *                    for four different serialisers.
 *      WORKAROUND 2  Query parameters read straight off `request.query_params`
 *                    with no serialiser, so the spec has no `parameters` block
 *                    for them at all: `?force` on the profile delete, and
 *                    `?shareholder_id` on the two import routes.
 *      WORKAROUND 3  The import routes' `file` / `file_two` multipart parts,
 *                    read from `request.FILES` and on no request schema — and
 *                    `/import/` documents only its JSON save body, so the
 *                    multipart commit has no generated type at all.
 *
 *    The DELETE-204 bug that used to sit here is FIXED upstream at `d15ae58`:
 *    the view now carries `responses={200: DeletedTransactionSerializer}` and
 *    `DeletedTransaction` is in `components.schemas`, so `deleteTransaction`
 *    uses the generated type and the hand-written one is gone.
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * A wire shape whose `K` fields the published spec names but the live API may not send yet (a
 * backend branch merged into the spec ahead of its deploy). The mapper reads them as optional, so
 * the screens work the same before and after the deploy.
 */
type Arriving<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

/**
 * The `?shareholder_id=` scope, omitted when the caller has no explicit
 * profile.
 *
 * Omitted means "the member's primary", which `primary_or_first` resolves as
 * `("-is_primary", "id")` — so with no row flagged primary it is the OLDEST
 * profile. Sending `shareholder_id=null` explicitly is accepted by the
 * serialiser and means the same thing, but the key is dropped instead so the
 * request URL says what the caller meant.
 *
 * An id the member does not own is 404 `SHAREHOLDER_NOT_FOUND` — deliberately
 * the same answer as "does not exist", so the UI can never probe for another
 * member's profiles.
 */
const scope = (shareholderId?: number) =>
  shareholderId === undefined ? {} : { shareholder_id: shareholderId }

/**
 * WORKAROUND 2 — a query parameter the generated operation does not admit.
 *
 * Two views read `request.query_params` directly, with no request serialiser
 * for drf-spectacular to introspect:
 *
 *   `DELETE /shareholders/{id}/`  reads `force` (`api/v5/portfolio/views.py`:
 *                                 `request.query_params.get("force", "")`)
 *   `POST /import/`, `/import/preview/`
 *                                 take `shareholder_id`, which every other
 *                                 portfolio route documents
 *
 * Both operations are therefore typed `query?: never`, and a caller has no way
 * to pass either. RUNTIME is unaffected — `createFinalURL` serialises
 * `params.query` whatever the type says — so only the type has to be carried
 * past the check, which is what the generic does: the result widens back to
 * `P`, the params type the operation DOES admit, and the extra key rides along
 * on the value rather than on the type. No assertion is needed, and none is
 * used — a spread is what makes the excess property legal, the same device the
 * cash-dividend edit body uses for `amount`.
 *
 * Deliberately NOT a fix in `schema.d.ts`: that file is regenerated wholesale
 * and a hand edit is silently reverted on the next `pnpm gen:api`.
 */
const withUndocumentedQuery = <P extends object>(
  params: P,
  query: Readonly<Record<string, string | number>>,
): P => ({ ...params, ...(Object.keys(query).length === 0 ? {} : { query }) })

/**
 * The `{group}` path segment for each kind, in the ONE direction that is safe.
 *
 * Read left to right: the app's word for a kind of transaction → the slug the
 * edit and delete routes want. Deliberately NOT derived from the create route
 * (`bonus-dividend` → `bonus_shares` is not a transformation) and never from a
 * label a component renders.
 */
export const TRANSACTION_GROUP_BY_KIND: Readonly<Record<string, TransactionGroup>> = {
  buy: 'purchases',
  bonus: 'bonus_shares',
  sell: 'sells',
  cashDividend: 'cash_dividends',
}

// ── Mappers ──────────────────────────────────────────────────────────────────
//
// Exported so each is testable on a fixture without a request. They map only —
// every value they produce is checked by the Zod schema at the call site, so a
// mapper never re-validates what the schema is about to.

export const mapProfile = (wire: Wire<'ShareholderProfile'>) => ({
  id: wire.id,
  name: wire.name,
  shareholderType: wire.shareholder_type,
  demat: wire.demat,
  isPrimary: wire.is_primary,
})

/**
 * Nulls are carried straight through, never coalesced.
 *
 * `market_value: null` means "this company has no current price", and a `?? 0`
 * anywhere on this path turns a Rs. 48,000 position of unknown value into a
 * Rs. 48,000 total loss. `investment` and `wacc` are non-null because cost is
 * always known — that is the whole shape of an unpriced row: cost known, value
 * unknown.
 */
export const mapHolding = (wire: Wire<'Holding'>) => ({
  symbol: wire.symbol,
  companyName: wire.company_name,
  sector: wire.sector,
  quantity: wire.quantity,
  wacc: wire.wacc,
  ltp: wire.ltp,
  previousClose: wire.previous_close,
  investment: wire.investment,
  marketValue: wire.market_value,
  unrealizedPl: wire.unrealized_pl,
  unrealizedPlPercent: wire.unrealized_pl_percent,
  dayChange: wire.day_change,
  dayChangePercent: wire.day_change_percent,
  weightPercent: wire.weight_percent,
  priceStatus: wire.price_status,
})

export const mapSummary = (wire: Wire<'PortfolioSummary'>) => ({
  holdingsCount: wire.holdings_count,
  totalUnits: wire.total_units,
  sectorCount: wire.sector_count,
  totalInvestment: wire.total_investment,
  totalMarketValue: wire.total_market_value,
  totalUnrealizedPl: wire.total_unrealized_pl,
  totalUnrealizedPlPercent: wire.total_unrealized_pl_percent,
  totalDailyPl: wire.total_daily_pl,
  totalDailyPlPercent: wire.total_daily_pl_percent,
  dividendReceived: wire.dividend_received,
  bestPerformer: wire.best_performer,
  worstPerformer: wire.worst_performer,
})

export const mapOverview = (wire: Wire<'PortfolioOverview'>) => ({
  summary: mapSummary(wire.summary),
  holdings: wire.holdings.map(mapHolding),
})

export const mapBuyRow = (wire: Wire<'BuyRow'>) => ({
  id: wire.id,
  symbol: wire.symbol,
  kind: wire.kind,
  quantity: wire.quantity,
  rate: wire.rate,
  date: wire.date,
  totalCost: wire.total_cost,
})

export const mapSellRow = (wire: Arriving<Wire<'SellRow'>, 'proceeds'>) => ({
  id: wire.id,
  symbol: wire.symbol,
  quantity: wire.quantity,
  price: wire.price,
  date: wire.date,
  proceeds: wire.proceeds == null ? null : mapSellProceeds(wire.proceeds),
})

export const mapBonusRow = (wire: Wire<'BonusRow'>) => ({
  id: wire.id,
  symbol: wire.symbol,
  quantity: wire.quantity,
  date: wire.date,
})

export const mapCashDividendRow = (wire: Arriving<Wire<'CashDividendRow'>, 'tax' | 'net'>) => ({
  id: wire.id,
  symbol: wire.symbol,
  amount: wire.amount,
  date: wire.date,
  tax: wire.tax ?? null,
  net: wire.net ?? null,
})

/**
 * The wire's four keys are `purchases`, `sells`, `bonus_shares`,
 * `cash_dividends` — a third spelling of the same four kinds, and the reason
 * the group union is declared once in `@/schemas/portfolio` rather than per
 * call site. All four are always present, even for a member with no profile.
 */
export const mapTransactions = (
  wire: Omit<Wire<'TransactionHistory'>, 'sells' | 'cash_dividends'> & {
    sells: readonly Parameters<typeof mapSellRow>[0][]
    cash_dividends: readonly Parameters<typeof mapCashDividendRow>[0][]
  },
) => ({
  purchases: wire.purchases.map(mapBuyRow),
  sells: wire.sells.map(mapSellRow),
  bonusShares: wire.bonus_shares.map(mapBonusRow),
  cashDividends: wire.cash_dividends.map(mapCashDividendRow),
})

export const mapProfitLossRow = (wire: Wire<'ProfitLossRow'>) => ({
  symbol: wire.symbol,
  sector: wire.sector,
  quantity: wire.quantity,
  investment: wire.investment,
  purchaseRate: wire.purchase_rate,
  ltp: wire.ltp,
  unrealizedGain: wire.unrealized_gain,
  realizedGain: wire.realized_gain,
  totalProfit: wire.total_profit,
})

export const mapProfitLoss = (wire: Wire<'ProfitLossReport'>) => ({
  rows: wire.rows.map(mapProfitLossRow),
  totalUnrealized: wire.total_unrealized,
  totalRealized: wire.total_realized,
  totalProfit: wire.total_profit,
})

export const mapTradeCosts = (wire: Wire<'TradeCosts'>) => ({
  amount: wire.amount,
  brokerCommission: wire.broker_commission,
  sebonFee: wire.sebon_fee,
  dpCharge: wire.dp_charge,
  totalCharges: wire.total_charges,
  netAmount: wire.net_amount,
  costPricePerShare: wire.cost_price_per_share,
})

export const mapBuyReceipt = (wire: Wire<'BuyReceipt'>) => ({
  id: wire.id,
  symbol: wire.symbol,
  kind: wire.kind,
  quantity: wire.quantity,
  rate: wire.rate,
  date: wire.date,
  costs: mapTradeCosts(wire.costs),
})

export const mapSellProceeds = (wire: Wire<'SellProceeds'>) => ({
  amount: wire.amount,
  brokerCommission: wire.broker_commission,
  sebonFee: wire.sebon_fee,
  dpCharge: wire.dp_charge,
  totalCharges: wire.total_charges,
  costBasis: wire.cost_basis,
  profitBeforeTax: wire.profit_before_tax,
  capitalGainTax: wire.capital_gain_tax,
  netReceivable: wire.net_receivable,
  netSellingPrice: wire.net_selling_price,
  profit: wire.profit,
})

export const mapSellReceipt = (wire: Wire<'SellReceipt'>) => ({
  id: wire.id,
  symbol: wire.symbol,
  quantity: wire.quantity,
  rate: wire.rate,
  date: wire.date,
  remainingUnits: wire.remaining_units,
  proceeds: mapSellProceeds(wire.proceeds),
})

export const mapDividendReceipt = (wire: Wire<'DividendReceipt'>) => ({
  id: wire.id,
  symbol: wire.symbol,
  amount: wire.amount,
  date: wire.date,
})

// ── Mappers for the six routes added in the v2 contract ─────────────────────

/**
 * One day on the value curve. Nothing here is nullable, which is a wart rather
 * than a convenience — `value_history` coalesces stored `None`s to `0`, so a
 * `"0.00"` point may be a day the cron could not price rather than a real zero.
 * The mapper carries the string through either way; it is not this layer's job
 * to guess which.
 */
export const mapSnapshotPoint = (wire: Wire<'SnapshotPoint'>) => ({
  date: wire.date,
  value: wire.value,
  invested: wire.invested,
  dayChange: wire.day_change,
  dayChangePercent: wire.day_change_percent,
  returnPercent: wire.return_percent,
  drawdownPercent: wire.drawdown_percent,
})

/**
 * The three return measures, nulls INTACT.
 *
 * There is no `?? 0` here and there must never be one. `null` means "could not
 * be computed" — too little history for a TWR, no snapshot for a turnover, no
 * sign change for an XIRR — and `Number(null)` is `0`, which would render a
 * fabricated 0 % as a real return. `twr` and `turnover` are null for every
 * member until the snapshot cron runs, so this is the ordinary case.
 *
 * `turnover` is also NOT scaled. It is a ratio (`"0.53"` = 0.53×), not a
 * percent, and multiplying by 100 here would put a 53 % return-looking figure
 * on a screen that means something else entirely.
 */
export const mapReturnMeasures = (wire: Wire<'ReturnMeasures'>) => ({
  xirr: wire.xirr,
  twr: wire.twr,
  turnover: wire.turnover,
})

export const mapSectorContribution = (wire: Wire<'SectorContribution'>) => ({
  sector: wire.sector,
  weightPercent: wire.weight_percent,
  dayPl: wire.day_pl,
  totalPl: wire.total_pl,
})

export const mapContributor = (wire: Wire<'Contributor'>) => ({
  symbol: wire.symbol,
  unrealizedPl: wire.unrealized_pl,
  unrealizedPlPercent: wire.unrealized_pl_percent,
})

export const mapReturnBreakdown = (wire: Wire<'ReturnBreakdown'>) => ({
  invested: wire.invested,
  unrealizedPl: wire.unrealized_pl,
  realizedPl: wire.realized_pl,
  dividends: wire.dividends,
  totalReturn: wire.total_return,
  totalReturnPercent: wire.total_return_percent,
  yieldOnCostPercent: wire.yield_on_cost_percent,
})

export const mapAnalytics = (wire: Wire<'AnalyticsReport'>) => ({
  returns: mapReturnMeasures(wire.returns),
  breakdown: mapReturnBreakdown(wire.breakdown),
  sectorContribution: wire.sector_contribution.map(mapSectorContribution),
  topGainers: wire.top_gainers.map(mapContributor),
  topLosers: wire.top_losers.map(mapContributor),
})

export const mapHealthCheck = (wire: Wire<'HealthCheck'>) => ({
  key: wire.key,
  label: wire.label,
  status: wire.status,
  detail: wire.detail,
})

/**
 * Three counters and six checks. There is NO score on the wire and none is
 * invented here — a derived 0–100 produced in this layer would be
 * indistinguishable from a contract value to every screen downstream, and the
 * backend supplies nothing to check it against.
 */
export const mapHealth = (wire: Wire<'HealthReport'>) => ({
  steady: wire.steady,
  watch: wire.watch,
  act: wire.act,
  checks: wire.checks.map(mapHealthCheck),
})

export const mapDividendIncome = (wire: Wire<'DividendIncome'>) => ({
  symbol: wire.symbol,
  companyName: wire.company_name,
  gross: wire.gross,
  tax: wire.tax,
  net: wire.net,
  paymentCount: wire.payment_count,
  lastDate: wire.last_date,
})

export const mapUpcomingIncome = (wire: Wire<'UpcomingIncome'>) => ({
  symbol: wire.symbol,
  companyName: wire.company_name,
  kind: wire.kind,
  detail: wire.detail,
  bookClosureDate: wire.book_closure_date,
})

export const mapIncome = (wire: Wire<'IncomeReport'>) => ({
  received: wire.received.map(mapDividendIncome),
  totalGross: wire.total_gross,
  totalTax: wire.total_tax,
  totalNet: wire.total_net,
  upcoming: wire.upcoming.map(mapUpcomingIncome),
})

export const mapImportedRow = (wire: Wire<'ImportedRow'>) => ({
  symbol: wire.symbol,
  kind: wire.kind,
  quantity: wire.quantity,
  price: wire.price,
  date: wire.date,
  notes: wire.notes,
})

/**
 * The exact `reason` `imports._drop_existing` gives a row the book already
 * holds. Free text with no code behind it (see BACKEND CHANGES NEEDED), so it
 * is compared HERE and nowhere else: if the backend rewords it, every such row
 * turns into an ordinary "not imported" line rather than vanishing.
 */
const ALREADY_IN_PORTFOLIO = 'already in portfolio'

export const mapSkippedRow = (wire: Wire<'SkippedRow'>) => ({
  symbol: wire.symbol,
  reason: wire.reason,
  alreadyInPortfolio: wire.reason === ALREADY_IN_PORTFOLIO,
})

export const mapDisputedRow = (wire: Wire<'DisputedRow'>) => ({
  symbol: wire.symbol,
  buyQuantity: wire.buy_quantity,
  sellQuantity: wire.sell_quantity,
})

export const mapNeedsDetailRow = (wire: Wire<'NeedsDetailRow'>) => ({
  symbol: wire.symbol,
  quantity: wire.quantity,
  date: wire.date,
  status: wire.status,
  candidates: wire.candidates.map((match) => ({ symbol: match.symbol, name: match.name })),
})

export const mapUncoveredRow = (wire: Wire<'UncoveredRow'>) => ({
  symbol: wire.symbol,
  wacc: wire.wacc,
})

/**
 * `needs_detail` minus any status this build predates. Dropped rather than
 * left to fail `needsDetailStatusSchema`, which would reject the WHOLE preview
 * over one hint — see `NEEDS_DETAIL_STATUSES` for why nothing is lost.
 */
const knownNeedsDetail = (rows: readonly Wire<'NeedsDetailRow'>[]) =>
  rows.filter((row) => needsDetailStatusSchema.safeParse(row.status).success)

export const mapImportPreview = (wire: Wire<'ImportPreview'>) => ({
  portal: wire.portal,
  rows: wire.rows.map(mapImportedRow),
  skipped: wire.skipped.map(mapSkippedRow),
  disputes: wire.disputes.map(mapDisputedRow),
  needsDetail: knownNeedsDetail(wire.needs_detail).map(mapNeedsDetailRow),
  uncovered: wire.uncovered.map(mapUncoveredRow),
})

export const mapImportResult = (wire: Wire<'ImportResult'>) => ({
  portal: wire.portal,
  imported: wire.imported,
  skipped: wire.skipped.map(mapSkippedRow),
  disputes: wire.disputes.map(mapDisputedRow),
  uncovered: wire.uncovered.map(mapUncoveredRow),
})

// ── Hand-typed bodies for what the OpenAPI document gets wrong ───────────────

/**
 * WORKAROUND 1 — `PATCH /transactions/{group}/{id}/`.
 *
 * The view picks its serialiser at RUNTIME from `_EDIT_SERIALIZERS[group]`, so
 * drf-spectacular sees one operation and documents one body:
 * `PatchedBuyEditRequest` (`quantity`, `price`, `date`, `kind`). Three of the
 * four groups take something else — `cash_dividends` wants `amount` and has no
 * `price`; `bonus_shares` takes only `quantity` and `date`.
 *
 * The damage is quiet, which is why it is worth a union: DRF's request
 * serialisers are plain `Serializer`s, so unknown keys are DROPPED rather than
 * refused. `{price: "120"}` sent to a cash dividend therefore becomes an empty
 * body, hits the "send at least one field" guard, and the member is told their
 * filled-in form is empty.
 *
 * `transactionEditDraftSchema` is the discriminated union that makes that
 * unrepresentable, and `editBody` below is the one place its output is widened
 * back to the single generated body type. The widening is the workaround, not a
 * shortcut: the union is what a caller sees, and the day the spec is fixed this
 * type alias becomes a union and `editBody`'s branches stop needing it.
 *
 * Note what the type system CANNOT do here: `PatchedBuyEditRequest` has no
 * `amount`, so the cash-dividend branch produces a key its declared return type
 * does not mention. That is not a bug in this file — it is the spec bug made as
 * visible as a type can make it, which is why the branch is annotated and
 * `EDIT_BODY_KEYS` exists for the test to assert against.
 */
type EditBodyWire = Wire<'PatchedBuyEditRequest'>

/**
 * The keys each group's edit body may carry, per `_EDIT_SERIALIZERS`.
 *
 * Declared as data so the test can prove the union and `editBody` agree with
 * the backend's four serialisers — a compile check cannot, because the
 * generated type describes only one of them.
 */
export const EDIT_BODY_KEYS: Readonly<Record<TransactionGroup, readonly string[]>> = {
  purchases: ['quantity', 'price', 'date', 'kind'],
  bonus_shares: ['quantity', 'date'],
  sells: ['quantity', 'price', 'date'],
  cash_dividends: ['amount', 'date'],
}

const editBody = (draft: TransactionEditDraft): EditBodyWire => {
  switch (draft.group) {
    case 'purchases':
      return {
        ...(draft.quantity === undefined ? {} : { quantity: draft.quantity }),
        ...(draft.price === undefined ? {} : { price: draft.price }),
        ...(draft.date === undefined ? {} : { date: draft.date }),
        ...(draft.kind === undefined ? {} : { kind: draft.kind }),
      }
    case 'bonus_shares':
      return {
        ...(draft.quantity === undefined ? {} : { quantity: draft.quantity }),
        ...(draft.date === undefined ? {} : { date: draft.date }),
      }
    case 'sells':
      return {
        ...(draft.quantity === undefined ? {} : { quantity: draft.quantity }),
        ...(draft.price === undefined ? {} : { price: draft.price }),
        ...(draft.date === undefined ? {} : { date: draft.date }),
      }
    case 'cash_dividends':
      // `amount` is on no generated type — see WORKAROUND 1. A spread is what
      // carries it past the excess-property check without an assertion that
      // would then read as a deliberate lie rather than a documented gap.
      return {
        ...(draft.amount === undefined ? {} : { amount: draft.amount }),
        ...(draft.date === undefined ? {} : { date: draft.date }),
      }
  }
}

/**
 * WORKAROUND 3 — the import routes' file parts.
 *
 * `ImportPreviewView` / `ImportView` declare
 * `parser_classes = [MultiPartParser, FormParser, JSONParser]` and then read
 * the uploads straight off `request.FILES`:
 *
 *     upload = request.FILES.get("file")
 *     second = request.FILES.get("file_two")
 *
 * Neither is a field on `ImportRequestSerializer`, so drf-spectacular cannot
 * see them and the generated `ImportRequest` carries only `portal`, `sheet`
 * and `sms_text` (and `/import/` is documented with the JSON save's body
 * instead, which has no file either). **A generated client has no file
 * parameter at all** — the one thing a preview exists to receive. So the body
 * is hand-built here as `FormData`, exactly the way the per-group PATCH body is
 * hand-built above, and for the same reason. BACKEND CHANGES NEEDED has the
 * one-line fix (`file = serializers.FileField(required=False)` on the request
 * serialiser).
 *
 * Runtime needs no help: `openapi-fetch`'s default body serialiser returns a
 * `FormData` untouched and DROPS the `Content-Type` header so the browser can
 * set its own multipart boundary. Only the TYPE has to be supplied.
 *
 * The part names are the backend's, not the app's: `file_two`, `sms_text`.
 * Optional parts are omitted rather than appended empty — `FormData` has no
 * `undefined`, and appending one would send the literal string "undefined".
 *
 * ⚠️ `combined_meroshare_tms` reads the two files in the counter-intuitive
 * order `parse_combined_meroshare_tms(meroshare_bytes=second, tms_bytes=first)`
 * — so `file` is the TMS **xlsx** and `fileTwo` is the MeroShare **csv**. A
 * form that labels them the other way round will parse the wrong file as the
 * wrong format and get 400 `IMPORT_FILE_UNREADABLE` for its trouble.
 *
 * Exported for the test, in the same spirit as `EDIT_BODY_KEYS`: jsdom does
 * not carry a `Blob`'s bytes or its filename through multipart encoding — a
 * part arrives named `"blob"` with the literal contents `"undefined"` — so the
 * only place which file landed in which part can be proven is here, before the
 * request. The wire test covers the part NAMES.
 */
export const importFormData = (draft: ImportDraft): FormData => {
  const form = new FormData()
  form.append('portal', draft.portal)
  if (draft.file !== undefined) form.append('file', draft.file)
  if (draft.fileTwo !== undefined) form.append('file_two', draft.fileTwo)
  if (draft.sheet !== undefined) form.append('sheet', draft.sheet)
  if (draft.smsText !== undefined) form.append('sms_text', draft.smsText)
  return form
}

/** The generated preview body, which describes none of the file parts. */
type ImportBodyWire = Wire<'ImportRequest'>

/**
 * The body `/import/` is DOCUMENTED with, for all three content types: the JSON
 * save. drf-spectacular reads one `request=` per view, so the multipart
 * single-file shape `importCommit` still sends — the WACC overlay — has no
 * generated type at all, and its `FormData` is carried past the check as this
 * type. The same WORKAROUND 3 gap, one route over.
 */
type ImportSaveBodyWire = Wire<'ImportSaveRequest'>

/**
 * App draft → the JSON save's wire body. Every key is sent on every row, `price`
 * as an explicit `null` when there is none: the serializer takes `null` and an
 * absent key alike (both save as Rs 0), and a uniform row reads the same in the
 * network tab as it does in the test. `date` is never null here — the draft
 * schema refuses a dateless row (see `importSaveRowSchema`).
 */
const importSaveBody = (draft: {
  portal: string
  rows: readonly ImportSaveRow[]
}): ImportSaveBodyWire => ({
  portal: draft.portal,
  rows: draft.rows.map((row) => ({
    symbol: row.symbol,
    kind: row.kind,
    quantity: row.quantity,
    price: row.price,
    date: row.date,
    notes: row.notes,
  })),
})

// ── The endpoints ────────────────────────────────────────────────────────────

export const portfolioService = {
  // ── 1-5. Shareholder profiles ─────────────────────────────────────────────

  /** Route 1. The member's profiles, primary first then oldest first. */
  listShareholders: async (): Promise<ShareholderProfile[]> => {
    const { rows } = await getPage<Wire<'ShareholderProfile'>>(() =>
      apiClient.GET('/api/v5/portfolio/shareholders/', {}),
    )
    return rows.map((row) => shareholderProfileSchema.parse(mapProfile(row)))
  },

  /**
   * Route 2. Create a profile — 201. The member's FIRST profile is made
   * primary; every later one is not.
   *
   * `demat` is not settable here (no field on `ProfileCreateRequest`), so a new
   * profile always comes back with `demat: null` and needs a PATCH to set one.
   * `shareholder_type` is sent explicitly rather than relying on the backend
   * default: the generated request type marks it required, and "Individual" is
   * the same value `ProfileCreateRequestSerializer` would have defaulted to.
   */
  createProfile: async (draft: ProfileCreateDraft): Promise<ShareholderProfile> => {
    const body = profileCreateDraftSchema.parse(draft)
    const data = await getData<Wire<'ShareholderProfile'>>(() =>
      apiClient.POST('/api/v5/portfolio/shareholders/', {
        body: {
          name: body.name,
          shareholder_type: body.shareholderType ?? 'Individual',
        },
      }),
    )
    return shareholderProfileSchema.parse(mapProfile(data))
  },

  /**
   * Route 3. Edit name / type / demat. At least one field, or 400.
   *
   * `demat` is forwarded only when the draft names it. `undefined` means "I
   * did not touch it" and the key is omitted; `""` CLEARS the number, and
   * `null` is accepted by the serialiser but is a no-op server side
   * (`update_profile` guards with `if demat is not None`) — so a form's "clear"
   * control must send `""`, never `null`.
   */
  updateProfile: async (id: number, draft: ProfileUpdateDraft): Promise<ShareholderProfile> => {
    const body = profileUpdateDraftSchema.parse(draft)
    const data = await getData<Wire<'ShareholderProfile'>>(() =>
      apiClient.PATCH('/api/v5/portfolio/shareholders/{id}/', {
        params: { path: { id } },
        body: {
          ...(body.name === undefined ? {} : { name: body.name }),
          ...(body.shareholderType === undefined ? {} : { shareholder_type: body.shareholderType }),
          ...(body.demat === undefined ? {} : { demat: body.demat }),
        },
      }),
    )
    return shareholderProfileSchema.parse(mapProfile(data))
  },

  /**
   * Route 4. Delete a profile — HTTP 200 with `{id, deleted}`, not 204.
   *
   * ── `force` is ADDITIVE. Nothing about the old behaviour changed. ─────────
   *
   * Without it, a profile that owns any buy, sell or dividend row is still
   * refused with 409 `SHAREHOLDER_HAS_TRANSACTIONS`, the same message, and the
   * row still survives. So the confirm dialog written against that refusal is
   * still correct — what is new is that there is now somewhere for it to go:
   * on the 409, offer a destructive confirmation and retry with `force: true`.
   *
   * `force` is a QUERY parameter, spelled exactly `?force=true`. A JSON body of
   * `{"force": true}` is IGNORED and you get the 409 — the same class of
   * mistake as putting `shareholder_id` in a write body. The backend accepts
   * `true | 1 | yes`, case-insensitively; this sends the one spelling.
   *
   * The forced delete destroys the profile's sells, buys, dividends, WACC cache
   * rows and the profile itself, in FK-safe order. IRREVERSIBLE: no soft
   * delete, no undo. And the response is UNCHANGED — `{id, deleted: true}`,
   * with NO COUNT of what was destroyed. A dialog that wants to say "this will
   * delete N transactions" must read `/transactions/` for N; there is nothing
   * in the 409 body or this response to count.
   *
   * `?force=true` on an already-empty profile is harmless and identical to no
   * force. Deleting the primary still promotes the oldest remaining profile;
   * deleting the last one leaves the member with none, i.e. the first-run state.
   *
   * The query parameter is on no generated operation — see WORKAROUND 2.
   */
  deleteProfile: async (id: number, force = false): Promise<DeletedProfile> => {
    const data = await getData<Wire<'DeletedProfile'>>(() =>
      apiClient.DELETE('/api/v5/portfolio/shareholders/{id}/', {
        params: withUndocumentedQuery({ path: { id } }, force ? { force: 'true' } : {}),
      }),
    )
    return deletedProfileSchema.parse(data)
  },

  /**
   * Route 5. Make this profile primary — 200 with the full profile.
   *
   * The response describes ONE profile, but every other profile's `is_primary`
   * changed too (`unset_primary_except`), so the caller must refetch the list
   * rather than patch this row in. Idempotent on the already-primary profile.
   */
  setPrimary: async (id: number): Promise<ShareholderProfile> => {
    const data = await getData<Wire<'ShareholderProfile'>>(() =>
      apiClient.POST('/api/v5/portfolio/shareholders/{id}/primary/', {
        params: { path: { id } },
      }),
    )
    return shareholderProfileSchema.parse(mapProfile(data))
  },

  // ── 6-10. Reads ───────────────────────────────────────────────────────────

  /**
   * Route 6. Open positions, biggest first and unpriced last.
   *
   * A member with no profile gets `data: []` and HTTP 200 — an empty book is an
   * answer, not an error. Fully-sold positions are absent entirely (they live
   * on in `/profit-loss/` with `quantity: 0`), so holdings rows and
   * profit-loss rows are different sets and must never be joined 1:1.
   */
  holdings: async (shareholderId?: number): Promise<Holding[]> => {
    const { rows } = await getPage<Wire<'Holding'>>(() =>
      apiClient.GET('/api/v5/portfolio/holdings/', { params: { query: scope(shareholderId) } }),
    )
    return rows.map((row) => holdingSchema.parse(mapHolding(row)))
  },

  /** Route 7. Portfolio-level totals. 200 with zeros and nulls when empty. */
  summary: async (shareholderId?: number): Promise<PortfolioSummary> => {
    const data = await getData<Wire<'PortfolioSummary'>>(() =>
      apiClient.GET('/api/v5/portfolio/summary/', { params: { query: scope(shareholderId) } }),
    )
    return portfolioSummarySchema.parse(mapSummary(data))
  },

  /**
   * Route 8. Summary AND holdings in one call.
   *
   * What anything needing both should read — the Home card, the portfolio
   * header, the allocation bar. Two separate reads are two computations over a
   * table a concurrent write may have moved between them, so the header could
   * show a total the rows below it do not add up to.
   */
  overview: async (shareholderId?: number): Promise<PortfolioOverview> => {
    const data = await getData<Wire<'PortfolioOverview'>>(() =>
      apiClient.GET('/api/v5/portfolio/overview/', { params: { query: scope(shareholderId) } }),
    )
    return portfolioOverviewSchema.parse(mapOverview(data))
  },

  /**
   * Route 9. The whole ledger, four arrays, newest first, UNPAGINATED.
   *
   * A single object, so the envelope's `meta` is `null` — there is no count.
   * See BACKEND CHANGES NEEDED: this will not scale.
   */
  transactions: async (shareholderId?: number): Promise<TransactionHistory> => {
    const data = await getData<Wire<'TransactionHistory'>>(() =>
      apiClient.GET('/api/v5/portfolio/transactions/', {
        params: { query: scope(shareholderId) },
      }),
    )
    return transactionHistorySchema.parse(mapTransactions(data))
  },

  /** Route 10. Realised and unrealised, open positions and closed ones. */
  profitLoss: async (shareholderId?: number): Promise<ProfitLossReport> => {
    const data = await getData<Wire<'ProfitLossReport'>>(() =>
      apiClient.GET('/api/v5/portfolio/profit-loss/', {
        params: { query: scope(shareholderId) },
      }),
    )
    return profitLossReportSchema.parse(mapProfitLoss(data))
  },

  // ── 16-19. The four reads added by the v2 contract ────────────────────────

  /**
   * Route 16. The value curve — oldest first, one point per calendar day a
   * snapshot exists for.
   *
   * `period` is always sent, including its default, so the request URL and the
   * query key say the same thing. `1m|3m|6m|1y|all` and nothing else; anything
   * else is 400 `VALIDATION_ERROR` on `fields.period`.
   *
   * ⚠️ AN EMPTY ARRAY IS THE NORMAL ANSWER TODAY, and it does NOT mean "no
   * holdings". `portfolio_daily_snapshot` has exactly one writer, the
   * management command `save_portfolio_snapshots`, and nothing in mm-be
   * schedules it — no celery beat, no crontab, no compose entry. So every
   * member gets `[]` until that is wired up, and history CANNOT be backfilled
   * afterwards (`build_snapshot` prices the book at today's prices; there is no
   * `--date`). A screen must tell "no holdings" and "no snapshot has ever been
   * taken" apart, and read one point as a value badge rather than a flat line.
   *
   * `meta.count` is dropped for the same reason `/holdings/` drops it: it is
   * `len(data)` server-side, so the array's own length is the same number from
   * a source that cannot disagree with the rows beside it.
   */
  valueHistory: async (
    period: ValueHistoryPeriod = DEFAULT_VALUE_HISTORY_PERIOD,
    shareholderId?: number,
  ): Promise<SnapshotPoint[]> => {
    const { rows } = await getPage<Wire<'SnapshotPoint'>>(() =>
      apiClient.GET('/api/v5/portfolio/value-history/', {
        params: { query: { period, ...scope(shareholderId) } },
      }),
    )
    return rows.map((row) => snapshotPointSchema.parse(mapSnapshotPoint(row)))
  },

  /**
   * Route 17. Return measures, sector contribution and the top five movers.
   *
   * Three metrics and three arrays — there is no Sharpe, beta, alpha,
   * volatility, benchmark or CAGR anywhere in `analytics.py`, so a tile for one
   * has nothing to read.
   *
   * All three of `returns.xirr`, `returns.twr` and `returns.turnover` are
   * genuinely nullable and arrive as `null` whenever they cannot be computed.
   * Today that is the ordinary case for two of the three: `twr` needs two
   * snapshots and `turnover` needs one, and nothing schedules the command that
   * writes them. Only `xirr` can work on day one. See `mapReturnMeasures`.
   */
  analytics: async (shareholderId?: number): Promise<AnalyticsReport> => {
    const data = await getData<Wire<'AnalyticsReport'>>(() =>
      apiClient.GET('/api/v5/portfolio/analytics/', {
        params: { query: scope(shareholderId) },
      }),
    )
    return analyticsReportSchema.parse(mapAnalytics(data))
  },

  /**
   * Route 18. Six traffic-light checks and the three counters over them.
   *
   * THERE IS NO SCORE — no 0–100, no grade, no percentage. `steady`, `watch`
   * and `act` are counts, and they sum to `checks.length` by construction.
   *
   * `checks: []` means the member has NO profile at all; an empty but real
   * profile answers with six `steady` checks. Two different states, and only
   * `checks.length` tells them apart.
   */
  health: async (shareholderId?: number): Promise<HealthReport> => {
    const data = await getData<Wire<'HealthReport'>>(() =>
      apiClient.GET('/api/v5/portfolio/health/', { params: { query: scope(shareholderId) } }),
    )
    return healthReportSchema.parse(mapHealth(data))
  },

  /**
   * Route 19. Dividends received, rolled up per company, plus the announced
   * corporate actions on companies still held.
   *
   * `totalGross`/`totalTax`/`totalNet` are `"0.00"` on an empty book where `summary.dividendReceived`
   * is `null` for the identical state — the same number with opposite empty
   * semantics, so no shared "is this empty?" helper across the two.
   */
  income: async (shareholderId?: number): Promise<IncomeReport> => {
    const data = await getData<Wire<'IncomeReport'>>(() =>
      apiClient.GET('/api/v5/portfolio/income/', { params: { query: scope(shareholderId) } }),
    )
    return incomeReportSchema.parse(mapIncome(data))
  },

  // ── 14-15. Import ─────────────────────────────────────────────────────────
  //
  // Three calls on two routes. The preview is multipart; `/import/` takes the
  // reviewed rows back as JSON (`importSave`, the trades) or one file again as
  // multipart (`importCommit`, the WACC overlay). `shareholder_id` is a query
  // parameter on all three, as on every other write. See WORKAROUND 2 and
  // WORKAROUND 3.

  /**
   * Route 14. Parse a portal export and show what WOULD be saved — 200, and it
   * writes nothing at all.
   *
   * `rows` is already filtered: unknown symbols, unparseable dates and rows the
   * book already holds have been moved into `skipped` before this returns. So a
   * row here is one that will be ATTEMPTED, not one that will succeed.
   *
   * The preview carries NO TOKEN — no preview id, no draft, no server-side
   * staging. The trades need none: `importSave` posts the reviewed rows back and
   * the server re-screens them. Only the WACC overlay is committed by uploading
   * its file again (`importCommit`), so only that `File` must outlive the
   * preview, and a page reload still loses it.
   *
   * A bad file is a coded 400 — `IMPORT_FILE_UNREADABLE`, `MISSING_COLUMNS`,
   * `IMPORT_FILE_TOO_LARGE`, `IMPORT_TOO_MANY_ROWS` — classified in `errors.ts`.
   *
   * Import is a WRITE: a member with no profile gets 404
   * `SHAREHOLDER_NOT_FOUND` here, not a 200-empty preview, and that is the
   * "create a profile first" prompt.
   */
  importPreview: async (draft: ImportDraft, shareholderId?: number): Promise<ImportPreview> => {
    const parsed = importDraftSchema.parse(draft)
    const form = importFormData(parsed)
    const data = await getData<Wire<'ImportPreview'>>(() =>
      apiClient.POST('/api/v5/portfolio/import/preview/', {
        params: withUndocumentedQuery({}, scope(shareholderId)),
        body: form as unknown as ImportBodyWire,
      }),
    )
    return importPreviewSchema.parse(mapImportPreview(data))
  },

  /**
   * Route 15, JSON. Save the rows the member reviewed — **201**, always.
   *
   * The server does not trust the list: `imports.commit_rows` re-screens,
   * re-dedupes and re-disputes it exactly as it would a file, so an edited row
   * cannot smuggle an unknown symbol or a duplicate in. Parsed first all the
   * same (`importSaveDraftSchema`), so a row the review page should have flagged
   * throws here rather than spending a request; `importRowErrors` is how that
   * page flags it first.
   *
   * A rejected row comes back as 400 `VALIDATION_ERROR` with `fields.rows` as a
   * LIST aligned to the rows sent — `portfolioFailure` lifts it into
   * `invalid.rowErrors`. A 201 is not "everything imported"; see `importCommit`.
   */
  importSave: async (draft: ImportSaveDraft, shareholderId?: number): Promise<ImportResult> => {
    const parsed = importSaveDraftSchema.parse(draft)
    const data = await getData<Wire<'ImportResult'>>(() =>
      apiClient.POST('/api/v5/portfolio/import/', {
        params: withUndocumentedQuery({}, scope(shareholderId)),
        body: importSaveBody(parsed),
      }),
    )
    return importResultSchema.parse(mapImportResult(data))
  },

  /**
   * Route 15, multipart. Upload one export and save it as parsed — **201**,
   * always. Used for the WACC overlay step: the report is applied on its own,
   * AFTER the trades are saved, and re-parsed against the book as it then is.
   *
   * The request runs in one transaction, so a crash saves nothing; but a row a
   * domain rule refuses (a sale beyond what is held) rolls back only its own
   * savepoint and is reported in `skipped` with the rule's message, and the rest
   * is saved. `docs/CHANGES_VS_LEGACY.md` §71 and the `imports.py` docstring call
   * the commit "all-or-nothing"; that is true of failures, not of refusals.
   * Partial success is the normal outcome, and the status is 201 even when
   * `imported` is 0 and every row was skipped.
   *
   * So there is no "the import failed" branch to write against a status code.
   * Always render `skipped`, and lead with
   * "{imported} imported, {skipped.length} skipped".
   *
   * The saved rows are NOT returned — refetch the book. Rows the book already
   * holds are skipped as `alreadyInPortfolio` (trade portals only — a WACC
   * overlay overwrites the average cost, so applying it twice is harmless).
   */
  importCommit: async (draft: ImportDraft, shareholderId?: number): Promise<ImportResult> => {
    const parsed = importDraftSchema.parse(draft)
    const form = importFormData(parsed)
    const data = await getData<Wire<'ImportResult'>>(() =>
      apiClient.POST('/api/v5/portfolio/import/', {
        params: withUndocumentedQuery({}, scope(shareholderId)),
        body: form as unknown as ImportSaveBodyWire,
      }),
    )
    return importResultSchema.parse(mapImportResult(data))
  },

  // ── 11-13b. The four writes ───────────────────────────────────────────────
  //
  // `shareholderId` goes in `params.query` on every one of these. Never in the
  // body — see note 1 at the top of this file.

  /**
   * Route 11. Record a buy lot — 201 with a `BuyReceipt`.
   *
   * `price` may carry up to 10 decimal places and is stored at that precision,
   * but every READ quantises to 2 dp: a lot bought at `505.1234567890` reads
   * back as `"505.12"` everywhere except `costs.costPricePerShare`.
   *
   * 404 `SHAREHOLDER_NOT_FOUND` when the member has no profile at all — the
   * asymmetry with reads, which answer 200-empty. Gate the affordance on
   * "has ≥1 profile" rather than letting the first buy discover it.
   */
  recordBuy: async (draft: BuyDraft, shareholderId?: number): Promise<BuyReceipt> => {
    const body = buyDraftSchema.parse(draft)
    const data = await getData<Wire<'BuyReceipt'>>(() =>
      apiClient.POST('/api/v5/portfolio/transactions/buy/', {
        params: { query: scope(shareholderId) },
        body: {
          symbol: body.symbol,
          quantity: body.quantity,
          price: body.price,
          date: body.date,
          kind: body.kind ?? 'SECONDARY',
        },
      }),
    )
    return buyReceiptSchema.parse(mapBuyReceipt(data))
  },

  /**
   * Route 12. Record a sale — 201 with a `SellReceipt`.
   *
   * 400 `INSUFFICIENT_QUANTITY` when `quantity` exceeds what is held, and the
   * error does NOT say how many units there are. Validate against `/holdings/`
   * first and treat the 400 as a race-condition backstop that should refetch
   * holdings. A full exit (`held === quantity`) is allowed and comes back with
   * `remainingUnits: 0`.
   */
  recordSell: async (draft: SellDraft, shareholderId?: number): Promise<SellReceipt> => {
    const body = sellDraftSchema.parse(draft)
    const data = await getData<Wire<'SellReceipt'>>(() =>
      apiClient.POST('/api/v5/portfolio/transactions/sell/', {
        params: { query: scope(shareholderId) },
        body: {
          symbol: body.symbol,
          quantity: body.quantity,
          price: body.price,
          date: body.date,
        },
      }),
    )
    return sellReceiptSchema.parse(mapSellReceipt(data))
  },

  /**
   * Route 13a. Record a cash dividend — 201. Note the route slug is
   * `cash-dividend/` (singular, hyphen) while its edit group is
   * `cash_dividends` (plural, underscore).
   */
  recordCashDividend: async (
    draft: CashDividendDraft,
    shareholderId?: number,
  ): Promise<DividendReceipt> => {
    const body = cashDividendDraftSchema.parse(draft)
    const data = await getData<Wire<'DividendReceipt'>>(() =>
      apiClient.POST('/api/v5/portfolio/transactions/cash-dividend/', {
        params: { query: scope(shareholderId) },
        body: { symbol: body.symbol, amount: body.amount, date: body.date },
      }),
    )
    return dividendReceiptSchema.parse(mapDividendReceipt(data))
  },

  /**
   * Route 13b. Record bonus units — 201, and it answers with a `BuyReceipt`,
   * not a receipt of its own: `record_bonus` delegates to `record_buy` with
   * `price=0, kind="BONUS"`, so every cost on it is `"0.00"`.
   *
   * Bonus units cost ZERO, not Rs. 100 par. They dilute the WACC — the total
   * cost stays fixed while the unit count rises — which is exactly what the
   * legacy engine got wrong. Nothing on this path may reintroduce a par value.
   *
   * Route slug `bonus-dividend/`; edit group `bonus_shares`.
   */
  recordBonus: async (draft: BonusDraft, shareholderId?: number): Promise<BuyReceipt> => {
    const body = bonusDraftSchema.parse(draft)
    const data = await getData<Wire<'BuyReceipt'>>(() =>
      apiClient.POST('/api/v5/portfolio/transactions/bonus-dividend/', {
        params: { query: scope(shareholderId) },
        body: { symbol: body.symbol, quantity: body.quantity, date: body.date },
      }),
    )
    return buyReceiptSchema.parse(mapBuyReceipt(data))
  },

  // ── 13c-13d. Edit and delete a transaction ────────────────────────────────
  //
  // Neither takes `shareholder_id`: ownership comes from the row itself
  // (`get_owned_row` filters `share_holder__created_by=user`), so an id that is
  // not the member's is 404 `TRANSACTION_NOT_FOUND`.

  /**
   * Route 13c. Edit a transaction — 200 with the ACK ONLY, never the row.
   *
   * The body is per-group and the generated type is not (WORKAROUND 1 above),
   * so the draft is a discriminated union and `editBody` narrows it. `group`
   * comes off the draft rather than being passed separately, which is what
   * stops a `cash_dividends` body being sent to a `purchases` path.
   *
   * An edit on `purchases`/`bonus_shares`/`sells` runs `rebuild_position`,
   * replaying FIFO and REPRICING every later sell's charges, CGT and realised
   * profit. So the caller must refetch the ledger, holdings, summary, overview
   * and profit-loss — see the invalidation map in `queries.ts`.
   */
  editTransaction: async (id: number, draft: TransactionEditDraft): Promise<UpdatedTransaction> => {
    const parsed = transactionEditDraftSchema.parse(draft)
    const data = await getData<Wire<'UpdatedTransaction'>>(() =>
      apiClient.PATCH('/api/v5/portfolio/transactions/{group}/{id}/', {
        params: { path: { group: parsed.group, id } },
        body: editBody(parsed),
      }),
    )
    return updatedTransactionSchema.parse(data)
  },

  /**
   * Route 13d. Delete a transaction — 200 with `{id, group, deleted}`.
   *
   * The generated type is now CORRECT here: `d15ae58` changed the view's
   * annotation to `responses={200: DeletedTransactionSerializer}`, which is
   * the dict form that stops drf-spectacular forcing a 204 onto a DELETE, and
   * `DeletedTransaction` is in `components.schemas`. The hand-written response
   * type that used to stand in for it is gone. The Zod parse stays: the
   * generated type describes the shape the server PROMISES, and this module's
   * job is to notice when the shape it SENDS differs.
   *
   * Deleting a sell RESTORES the units. Deleting a buy whose units have already
   * been sold is refused with 400 `INSUFFICIENT_QUANTITY` — the same code as an
   * oversell, on a completely different screen, which is why the error mapper
   * takes the operation into account rather than the code alone.
   */
  deleteTransaction: async (group: TransactionGroup, id: number): Promise<DeletedTransaction> => {
    const data = await getData<Wire<'DeletedTransaction'>>(() =>
      apiClient.DELETE('/api/v5/portfolio/transactions/{group}/{id}/', {
        params: { path: { group, id } },
      }),
    )
    return deletedTransactionSchema.parse(data)
  },
}
